import { useRef, type ChangeEvent } from "react";
import { FileText, ImagePlus, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/hooks/use-translations";
import { WorkspacePanelHeading } from "@/components/workspace/workspace-panel-heading";

interface WorkspaceResourcesHeaderProps {
	isUploading: boolean;
	uploadDisabled: boolean;
	onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
	onOpenCreatives: () => void;
	isGeneratingPresentation: boolean;
	presentationDisabled: boolean;
	onGeneratePresentation: () => void;
}

/** Resources title with the upload, creatives and presentation actions. */
export function WorkspaceResourcesHeader({
	isUploading,
	uploadDisabled,
	onFileChange,
	onOpenCreatives,
	isGeneratingPresentation,
	presentationDisabled,
	onGeneratePresentation,
}: WorkspaceResourcesHeaderProps) {
	const t = useTranslations("workspace");
	const fileInputRef = useRef<HTMLInputElement>(null);

	return (
		<WorkspacePanelHeading title={t("resources")}>
			<div className="flex gap-2 mt-6">
				<Button
					variant="secondary"
					onClick={() => fileInputRef.current?.click()}
					disabled={uploadDisabled}
					className="flex-1 min-w-0 px-3"
				>
					{isUploading ? (
						<Loader2 size={14} className="animate-spin" />
					) : (
						<Plus size={14} />
					)}
					<span className="truncate">
						{isUploading ? "Uploading..." : t("addResources")}
					</span>
				</Button>
				<Button
					variant="secondary"
					onClick={onOpenCreatives}
					className="flex-1 min-w-0 px-3"
				>
					<ImagePlus size={14} />
					<span className="truncate">Creatives</span>
				</Button>
			</div>
			<input
				ref={fileInputRef}
				type="file"
				multiple
				className="hidden"
				onChange={onFileChange}
			/>
			<Button
				variant="secondary"
				onClick={onGeneratePresentation}
				disabled={presentationDisabled}
				className="w-full mt-2 px-3"
			>
				{isGeneratingPresentation ? (
					<Loader2 size={14} className="animate-spin" />
				) : (
					<FileText size={14} />
				)}
				{isGeneratingPresentation ? "Starting..." : "Generate Presentation"}
			</Button>
		</WorkspacePanelHeading>
	);
}
